import React, { useEffect, useState, MouseEvent } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import CardActions from '@mui/material/CardActions';
import CommentIcon from '@mui/icons-material/ModeComment';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import CommentSection from './CommentSection/CommentSection';
import Comment, { CommentT } from './CommentSection/Comment';
import { getHeightOfRows } from '../WidgetsLayout/helpers';
import './_chartActions.scss';
import {
    useCreateComment,
    useDeleteComment,
    useGetComments,
} from './CommentSection/hooks/comments';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import { useParams } from 'react-router-dom';
import useGetDashboard from 'routes/Explore/hooks/useGetDashboard';

type Props = {
    canEdit: boolean;
    isExpanded: boolean;
    toggleExpand: () => void;
    chartKey: number;
    // below fetched from store
    isAdmin: boolean;
    userEmail: string | null;
};

export type ChartActionT = typeof ChartActions;

const ChartActions = ({
    canEdit,
    isExpanded,
    toggleExpand,
    chartKey,
    isAdmin,
    userEmail,
}: Props) => {
    const { slug: slugParam } = useParams();
    const publicToken = getPublicToken();
    const { data: dashboard } = useGetDashboard(slugParam, publicToken);
    const dashboardKey = dashboard!.key;
    const [comments, setComments] = useState<CommentT[]>([]);

    const { data, isLoading, isError } = useGetComments(chartKey, dashboardKey);

    const { mutate: mutateCreateComment } = useCreateComment(
        setComments,
        chartKey,
        dashboardKey,
    );

    const { mutate: mutateDeleteComment } = useDeleteComment(
        setComments,
        chartKey,
        dashboardKey,
    );

    useEffect(() => {
        if (!isLoading && !isError && data) {
            setComments(data);
        }
    }, [data, isLoading, isError]);

    const submitComment = (content: string) => {
        if (content) {
            mutateCreateComment(content);
        }
    };

    const deleteComment = (commentId: number) => {
        if (commentId) {
            mutateDeleteComment(commentId);
        }
    };

    const handleExpandClick = (e: MouseEvent) => {
        toggleExpand();
        e.preventDefault();
    };

    // If the button is expanded, we want to leave it visible.
    const buttonClassName = classNames('show-comment-button-hide', {
        'comment-button-expanded': isExpanded,
    });

    return (
        <>
            <CardActions className="grid-chart-actions">
                <div className={buttonClassName}>
                    <IconButton
                        onClick={handleExpandClick}
                        className="e2e-show-comment-button"
                        color="primary"
                        size="large"
                    >
                        <Badge badgeContent={comments.length}>
                            {isExpanded ? (
                                <InsertCommentIcon fontSize="small" />
                            ) : (
                                <CommentIcon fontSize="small" />
                            )}
                        </Badge>
                    </IconButton>
                </div>
            </CardActions>

            <CommentSection
                submitComment={submitComment}
                canEdit={canEdit}
                show={isExpanded}
                maxHeight={getHeightOfRows(comments.length)}
            >
                {comments.map((comment) => {
                    const showDeleteButton =
                        isAdmin || comment.author.email === userEmail;
                    return (
                        <Comment
                            key={comment.id}
                            showDeleteButton={canEdit && showDeleteButton}
                            comment={comment}
                            deleteComment={deleteComment}
                        />
                    );
                })}
            </CommentSection>
        </>
    );
};

// @ts-expect-error [TS migration] Was not detected by flow
const mapStateToProps = ({ vision }, props) => ({
    ...props,
    isAdmin: vision.user ? vision.user.isAdmin : false,
    userEmail: vision.user ? vision.user.email : null,
});

export default connect(mapStateToProps)(ChartActions);
